import { action, computed } from 'mobx';
import { TaskRepresentation } from 'src/generated-api-client';
import { taskManagerApi } from 'src/services/apiServices';
import { EntityProvider } from 'src/utils/EntityProvider';
import { AsyncOperationWithStatus } from 'src/utils/mobx/AsyncOperationWithStatus';
import { RequestHelper } from 'src/utils/RequestHelper';

export class TaskStoreClass {
    constructor(private taskId: string) {}

    taskLoader = new AsyncOperationWithStatus(
        (...args: any[]) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise<TaskRepresentation>(
                taskManagerApi.getTaskInfo({ id: this.taskId }, options),
            );
        },
        { defaultIsLoading: true },
    );

    taskVariablesLoader = new AsyncOperationWithStatus(
        (...args: any[]) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise<{ [key: string]: any }>(
                taskManagerApi.getTaskFormVariables(
                    { id: this.taskId },
                    options,
                ),
            );
        },
        { defaultIsLoading: true },
    );

    submitTaskFormLoader = new AsyncOperationWithStatus(
        (data: { [key: string]: any }, ...args: any[]) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                taskManagerApi.submitForm(
                    { id: this.taskId, requestBody: data },
                    options,
                ),
            );
        },
    );

    @computed
    get taskVariables() {
        return this.taskVariablesLoader.data;
    }

    @computed
    get task() {
        return this.taskLoader.data;
    }

    @computed
    get isLoading() {
        return this.taskLoader.isLoading || this.taskVariablesLoader.isLoading;
    }

    @computed
    get isLoadingVariables() {
        return this.taskVariablesLoader.isLoading;
    }

    @computed
    get isLoadingSubmitTaskForm() {
        return this.submitTaskFormLoader.isLoading;
    }

    @action
    async loadTask() {
        await this.taskLoader.call();
    }

    @action
    async loadTaskVariables() {
        await this.taskVariablesLoader.call();
    }

    @action
    loadTaskAndVariables() {
        return Promise.all([
            this.taskLoader.call(),
            this.taskVariablesLoader.call(),
        ]);
    }

    @action
    submitTask(data: { [key: string]: any }) {
        return this.submitTaskFormLoader.call(data);
    }

    @action
    async reset() {
        await this.taskLoader.reset();
        await this.taskVariablesLoader.reset();
        await this.submitTaskFormLoader.reset();
    }
}

export const TaskStore = new EntityProvider<string, TaskStoreClass>(
    TaskStoreClass,
);
